import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { MODALS_ID, QUERY_KEY } from '../../../constants'
import type { components } from '../../../constants/schema'

import { toast, clipboardCopy } from '../../../utils'
import { abrViewPromotionPage } from '../../../utils/abr'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useNavigate } from '../../../hooks/useNavigate'
import useParamId from '../../../hooks/useParamId'

import { getEventDetail, approveEvent } from '../../../api/event'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { CenterWrap, AlignCenterItem, RowBetween } from '../../../components/common'
import { Header } from '../../../components/header'
import { Footer } from '../../../components/footer'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { Spinner } from '../../../components/spinner/Spinner'
import { ArtipioContentsHeader, ArtipioContents, ArtipioContentItem } from '../../../components/contents'
import { TermsDetailModal } from '../../../components/modals/TermsDetailModal'

import { ReactComponent as HomeIcon } from '../../../assets/ico/detail/home_black.svg'
import { ReactComponent as ShareIcon } from '../../../assets/ico/detail/share_black.svg'
import { ReactComponent as CheckedIcon } from '../../../assets/ico/common/check/check_checked_blue_fill.svg'
import { ReactComponent as UncheckedIcon } from '../../../assets/ico/common/check/check_default_gray_fill.svg'

type Term = components['schemas']['TermsDto']

export function EventDetail() {
  const {
    commonStore: { setShowTab },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [selectTerms, setSelectTerms] = useState<Term[]>([])

  const queryClient = useQueryClient()
  const eventId = useParamId()
  const navigate = useNavigate()

  const {
    data: event,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.event, { eventId }],
    queryFn: async () => await getEventDetail(eventId as number),
    enabled: typeof eventId === 'number',
  })
  const { mutate } = useMutation({
    mutationFn: approveEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.event, { eventId }] })
      toast('이벤트 참여가 완료되었어요')
    },
    onError: (error) => {
      if (isAxiosError(error)) toast(error.response?.data.message)
    },
  })

  const HEAD_INFO = {
    title: event?.meta?.title ?? '이벤트 상세',
    description: event?.meta?.description ?? '',
    ogTitle: event?.meta?.ogTitle ?? '',
    ogDescription: event?.meta?.ogDescription ?? '',
    ogUrl: window.location.href,
  }

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (!event) return

    abrViewPromotionPage(event ?? {})
    ChannelService.track('view_promotion_page')
  }, [event])

  if (typeof eventId === 'undefined' || isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const isTerms =
    !!event.terms &&
    event.terms.length > 0 &&
    typeof event.overallStatus !== 'undefined' &&
    event.overallStatus !== 'UNKNOWN'

  // 필수인 약관
  const requiredTerms = event.terms?.filter((terms) => terms.required) ?? []
  // 유저가 이미 동의한 약관 중 필수인 약관
  const requiredAgreedUserTerms =
    event.userTerms?.filter(
      ({ id, approved }) => (event.terms?.findIndex((terms) => terms.id === id) ?? 0) > -1 && approved,
    ) ?? []
  // 유저가 선택한 약관 중 필수인 약관
  const selectRequiredTerms = selectTerms.filter((terms) => terms.required)

  // 로그인을 안 했으면 able
  // 참여 가능 상태가 아니거나 필수 약관 중 사용자가 기존에 동의 하지 않은 약관(어딘가에서 미리 체크한 약관)의 개수와 사용자가 체크한 약관의 개수가 다르면 disabled
  const eventButtonDisabled =
    event.overallStatus === 'NEED_LOGIN'
      ? false
      : event.overallStatus !== 'CAN_APPROVE' ||
        requiredTerms.length - requiredAgreedUserTerms.length !== selectRequiredTerms.length

  const participateEvent = async () => {
    if (event.overallStatus === 'NEED_LOGIN') {
      navigate(`/signIn`)
      return
    }

    if (event.terms && event.terms.length !== selectTerms.length) {
      toast('모든 (선택) 약관에 동의해 주세요')
      return
    }

    mutate({ eventId, data: { terms: selectTerms.map(({ id }) => ({ termsId: id, approved: true })) } })
  }

  const openTermsDetailModal = (termsId: number) => {
    const close = () => {
      closeModal(MODALS_ID.TERMS_DETAIL)
    }

    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => <TermsDetailModal termsId={termsId} onDismiss={close} />,
    })
  }

  const goHome = () => navigate('/home')

  const copyUrl = () =>
    clipboardCopy(window.location.href, { message: '이벤트 페이지 주소가 복사되었어요.', isFooter: eventId === 61 })

  const redirectEvent = (id: number) => {
    switch (id) {
      case 65:
        window.location.href =
          'https://booking.naver.com/booking/12/bizes/1473343/items/6972778?startDateTime=2025-08-12T00%3A00%3A00%2B09%3A00'
        return
      case 66:
        window.location.href = 'https://smartstore.naver.com/artipio/products/12533340200'
        return
      case 69:
        window.location.href = 'https://pf.kakao.com/_sWLZxj'
        return
    }
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header
          type="SUB"
          rightItem={
            <HeaderRightItem $gap={16}>
              <ShareIcon onClick={copyUrl} />
              <HomeIcon onClick={goHome} />
            </HeaderRightItem>
          }
        />
        <StyledContent
          isLastImg={event.contents?.[(event.contents?.length as number) - 1]?.type === 'image'}
          isTerms={isTerms}
        >
          <ArtipioContentsHeader>
            <ArtipioContentsHeader.Title>{event.title}</ArtipioContentsHeader.Title>
            <ArtipioContentsHeader.Date>
              {dayjs(event.createdDate ?? '').format('YYYY.MM.DD')}
            </ArtipioContentsHeader.Date>
          </ArtipioContentsHeader>
          <ArtipioContents>
            {event.contents?.map((content, i) => {
              const isImage = content.type === 'image'
              const isFirstImg =
                (isImage && i === 0) ||
                ['image', 'imageLink'].includes(event.contents?.[i - 1]?.type ?? '') ||
                ['image', 'imageLink'].includes(event.contents?.[i + 1]?.type ?? '')
              const isLastImg =
                (isImage && i === Number(event.contents?.length) - 1) ||
                ['image', 'imageLink'].includes(event.contents?.[i + 1]?.type ?? '')
              console.log({ isImage, isFirstImg, isLastImg })
              return <ArtipioContentItem key={i} {...content} isFirstImg={isFirstImg} isLastImg={isLastImg} />
            })}
          </ArtipioContents>
        </StyledContent>
        {process.env.NODE_ENV === 'production' && eventId === 61 && (
          <Footer>
            <Button type="primary" size="large" block={true} onClick={copyUrl}>
              이벤트 공유하기
            </Button>
          </Footer>
        )}
        {process.env.NODE_ENV === 'production' && eventId === 65 && (
          <Footer>
            <Button type="primary" size="large" block={true} onClick={() => redirectEvent(65)}>
              바로 구매
            </Button>
          </Footer>
        )}
        {process.env.NODE_ENV === 'production' && eventId === 66 && (
          <Footer>
            <Button type="primary" size="large" block={true} onClick={() => redirectEvent(66)}>
              바로 구매
            </Button>
          </Footer>
        )}
        {process.env.NODE_ENV === 'production' && eventId === 69 && (
          <Footer>
            <Button type="primary" size="large" block={true} onClick={() => redirectEvent(69)}>
              채널 추가 바로가기
            </Button>
          </Footer>
        )}
        {isTerms && (
          <Footer>
            {event.overallStatus !== 'ALREADY_APPROVED' && (
              <TermsList>
                {event.terms?.map((term) => {
                  const { id, name, required } = term
                  const isAgreed =
                    (event.userTerms && event.userTerms?.findIndex((v) => v.id === id && v.approved) > -1) ||
                    selectTerms.findIndex((v) => v.id === id) > -1

                  const toggleTerm = () => {
                    if (event.overallStatus !== 'CAN_APPROVE') return

                    setSelectTerms((prevState) =>
                      isAgreed ? prevState.filter((v) => v.id !== id) : [...prevState, { ...term }],
                    )
                  }

                  const handleOpenTermsModal = () => openTermsDetailModal(id)

                  return (
                    <TermItem key={id}>
                      <AlignCenterItem $gap={8} onClick={toggleTerm}>
                        {isAgreed ? <CheckedIcon /> : <UncheckedIcon />}
                        <Text size={14}>{`(${required ? '필수' : '선택'}) ${name}`}</Text>
                      </AlignCenterItem>
                      <Text size={12} color="text_secondary" onClick={handleOpenTermsModal}>
                        보기
                      </Text>
                    </TermItem>
                  )
                })}
              </TermsList>
            )}
            <Button
              type={event.overallStatus === 'NEED_LOGIN' ? 'dark' : 'primary'}
              size="large"
              disabled={eventButtonDisabled}
              block={true}
              onClick={participateEvent}
            >
              {event.overallStatusLabels}
            </Button>
          </Footer>
        )}
      </Wrap>
    </>
  )
}

const HeaderRightItem = styled(RowBetween)`
  > * {
    cursor: pointer;
  }
`

const StyledContent = styled(Content)<{ isLastImg: boolean; isTerms: boolean }>`
  ${({ isLastImg, isTerms }) => (!isLastImg || isTerms ? 'padding-bottom: 108px;' : '')}
`

const TermsList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 24px 0 8px;
`

const TermItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  list-style: none;
  > span {
    text-decoration: underline;
  }
`
